import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import SnackBar from 'components/common/snack-bar'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import Typo from 'constants/typography'
import clsx from 'clsx'
import { hasLocizeTranslation } from 'lib/utils/locize'
import compose from 'recompose/compose'
import { withI18next } from 'lib/i18n/withI18next'
import DailyCheckInApiHandler from 'lib/api/daily-check-in'
import styles from './daily-task-notification.scss'
import dailyCheckInDuck from '../duck'

const DailyTaskNotification = ({
  t,
  isGuestMode,
  dailyCheckIn,
  initDailyCheckInState,
}) => {
  const [showSnack, setShowSnack] = useState(false)
  const [isDailyCheckInCompleted, setIsDailyCheckInCompleted] = useState(false)
  const [completedReward, setCompletedRewards] = useState(undefined)

  const snackTitle = isDailyCheckInCompleted
    ? hasLocizeTranslation(t, 'DAILY_COMPLETE', `Daily check-in complete!`)
    : `${hasLocizeTranslation(t, 'TASK_COMPLETE', `Task complete: `)}${
        completedReward?.title
      }`
  const snackSubTitle = isDailyCheckInCompleted
    ? hasLocizeTranslation(
        t,
        'COME_BACK_TOMORROW',
        `Come back tomorrow to continue your streak.`,
      )
    : hasLocizeTranslation(
        t,
        'CHECK_REWARDS',
        `Check your rewards section for more.`,
      )

  const isDisabledDailyCheckIn =
    dailyCheckIn?.message === 'Daily-Check-In is disabled'

  useEffect(() => {
    const fetchDailyCheckInState = async () => {
      try {
        const rest = await DailyCheckInApiHandler.getDailyCheckIn()
        if (rest) {
          initDailyCheckInState(rest)
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error('Failed to preload daily check-in data', error)
      }
    }

    if (!isGuestMode) {
      fetchDailyCheckInState()
    }
  }, [isGuestMode, initDailyCheckInState])

  useEffect(() => {
    const currentDay = dailyCheckIn?.current_day
    const currentEvent = dailyCheckIn?.current_day_event
    const isPending = dailyCheckIn?.current_day_status === 'pending'

    const isOpenApplication = currentEvent === 'Application Opened'

    const updateTask = async () => {
      try {
        const res = await DailyCheckInApiHandler.updateDailyCheckIn({
          day: currentDay,
          event_name: currentEvent,
        })
        initDailyCheckInState(res)

        setIsDailyCheckInCompleted(true)
        setShowSnack(true)
      } catch (error) {
        console.error(error) // eslint-disable-line
        setShowSnack(false)
      }
    }

    if (isPending && isOpenApplication && !isDisabledDailyCheckIn) {
      updateTask()
    }
  }, [dailyCheckIn, initDailyCheckInState, isDisabledDailyCheckIn])

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'RUDDER_UPDATE') return

      const { eventName } = event.data
      const currentDay = dailyCheckIn?.current_day
      const currentEvent = dailyCheckIn?.current_day_event
      const isPending = dailyCheckIn?.current_day_status === 'pending'
      const isDailyCheckInTask = isPending && currentEvent === eventName

      const isMoreRewardTask = dailyCheckIn?.more_rewards?.find(
        (reward) => reward?.event_name === eventName,
      )

      if (isDisabledDailyCheckIn) return
      if (!isDailyCheckInTask && !isMoreRewardTask) return

      const payload = {}
      if (isDailyCheckInTask) {
        payload.day = currentDay
        payload.event_name = currentEvent
      }
      if (isMoreRewardTask) {
        payload.more_rewards_event_name = isMoreRewardTask.event_name
      }

      try {
        const res = await DailyCheckInApiHandler.updateDailyCheckIn(payload)
        initDailyCheckInState(res)

        if (isDailyCheckInTask) setIsDailyCheckInCompleted(true)
        if (isMoreRewardTask) setCompletedRewards(isMoreRewardTask)
        setShowSnack(true)
      } catch (error) {
        console.error(error) // eslint-disable-line
        setShowSnack(false)
      }
    }

    if (!isGuestMode) {
      window.addEventListener('message', handleMessage)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [isGuestMode, initDailyCheckInState, dailyCheckIn, isDisabledDailyCheckIn])

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      {showSnack && (
        <div className="daily-task-notification">
          <SnackBar
            className="daily-task-notification__snack"
            open={showSnack}
            duration={2000}
            onClose={() => {
              setShowSnack(false)
              setCompletedRewards(false)
              setIsDailyCheckInCompleted(false)
            }}
          >
            <div className="daily-task-notification__snack__content">
              <Icon src={ICONS.complete} size={ICON_SIZE.large} />
              <div>
                <div className={clsx(Typo.subtitle2)}>{snackTitle}</div>
                <div className={clsx(Typo.caption)}>{snackSubTitle}</div>
              </div>
            </div>
          </SnackBar>
        </div>
      )}
    </React.Fragment>
  )
}

DailyTaskNotification.defaultProps = {
  dailyCheckIn: {},
}

DailyTaskNotification.propTypes = {
  t: PropTypes.func.isRequired,
  isGuestMode: PropTypes.bool.isRequired,
  initDailyCheckInState: PropTypes.func.isRequired,
  dailyCheckIn: PropTypes.shape({
    message: PropTypes.string,
    current_day: PropTypes.number,
    current_day_event: PropTypes.string,
    current_day_status: PropTypes.string,
    days: PropTypes.arrayOf(PropTypes.shape({})),
    more_rewards: PropTypes.arrayOf(PropTypes.shape({})),
  }),
}

export default compose(
  connect(
    (state) => ({
      isPhone: state.device.isPhone,
      dailyCheckIn: state.dailyCheckIn,
      isGuestMode: state.auth.isGuestMode,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          initDailyCheckInState:
            dailyCheckInDuck.creators.initDailyCheckInState,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(DailyTaskNotification)
